import { XMLSerializer } from '@xmldom/xmldom';
import uuid from 'uuid';
import interactionParserUtils from './InteractionParserUtils';
import graphicGapMatchParser from './GraphicGapMatchParser';
import utilService from '../../../../services/UtilService';
import { applyInsperaNamespaceStyle, isInsperaNamespacedStyleBodyElement, replaceMediaWithPlaceholder } from '../itemBody';
import { parseImage } from '../MediaModels';
import customFieldsService from '../../../../services/CustomFieldsService';
import sessionReader from '../../../../store/readHelpers/SessionReader';
import settingsReader from '../../../../store/readHelpers/SettingsReader';

const serializer = new XMLSerializer();

const gapMatchParser = {
    /**
     * @param {Element} rootElement Will be modified!
     * @param {string} language
     * @param {string} defaultLanguage
     * @returns {Array}
     */
    extractGapTextModels(rootElement, language, defaultLanguage) {
        const gapTexts = Array.from(rootElement.getElementsByTagName('gapText'));
        const gapTextModels = gapTexts.map((gapText) => {
            const gapTextModel = interactionParserUtils.getNonLanguageAttributes(gapText);
            gapTextModel.textContent = interactionParserUtils.getNodeContent(gapText, language, defaultLanguage);
            gapTextModel.matchMax = utilService.getIntOr(gapTextModel.matchMax);

            return gapTextModel;
        });

        gapTexts.forEach((gapTextElement) => {
            gapTextElement.parentNode.removeChild(gapTextElement);
        });

        return gapTextModels;
    },

    extractGapImgModels(interactionNode, language, defaultLanguage, assessmentRunId, questionIdentifier, mediaInfo) {
        return graphicGapMatchParser
            .extractGapImgModels(interactionNode, language, defaultLanguage, assessmentRunId, questionIdentifier, mediaInfo);
    },

    /**
     * @param {Element} currentLanguageBlockquote Will be modified!
     * @returns {*|Array}
     */
    extractGapModels(currentLanguageBlockquote) {
        const doc = currentLanguageBlockquote.ownerDocument;
        const gaps = Array.from(currentLanguageBlockquote.getElementsByTagName('gap'));

        return gaps.map((gapElement) => {
            const placeholderId = `gap-placeholder-${uuid.v4()}`;
            const identifier = gapElement.getAttribute('identifier');
            const gapModel = { placeholderId, identifier };
            const placeholder = doc.createElement('span');
            placeholder.textContent = ' ';
            placeholder.setAttribute('id', placeholderId);
            placeholder.setAttribute('class', 'gap-placeholder');
            gapElement.parentNode.replaceChild(placeholder, gapElement);

            return gapModel;
        });
    },

    extractPrompt(rootElement, language, defaultLanguage) {
        const prompt = interactionParserUtils.getPrompt(rootElement, language, defaultLanguage);

        Array.from(
            rootElement.getElementsByTagName('prompt')
        )
            .forEach((promptElement) => {
                promptElement.parentNode.removeChild(promptElement);
            });

        return prompt;
    },

    extractTokenHeader(rootElement, language, defaultLanguage) {
        let element = rootElement.firstChild;
        while (element.nodeType !== 1) {
            element = element.nextSibling;
        }
        if (element.nodeName.startsWith('h')) {
            element.parentNode.removeChild(element);
            return interactionParserUtils.getNodeContent(
                element,
                language,
                defaultLanguage
            );
        }

        return undefined;
    },

    parseGapMatchBody(element) {
        Array.from(element.childNodes).forEach((node) => {
            if (node.nodeType !== 1) {
                // non elements should be ignored
                return undefined;
            }
            if (isInsperaNamespacedStyleBodyElement(node)) {
                applyInsperaNamespaceStyle(node);
            }

            if (node.hasChildNodes()) {
                return this.parseGapMatchBody(node);
            }

            return undefined;
        });
    },

    extractInteractionBody(rootElement, language, defaultLanguage) {
        const languageBlocks = rootElement.getElementsByTagName('blockquote');

        if (defaultLanguage !== '' && language !== defaultLanguage) {
            let currentLanguageBlockquote;
            Array.from(languageBlocks).forEach((block) => {
                if (block.hasAttribute('lang') && block.hasAttribute('style')) {
                    block.removeAttribute('style');
                }
                if (block.getAttribute('lang') === language) {
                    currentLanguageBlockquote = block;
                }
            });
            return currentLanguageBlockquote;
        } else if (languageBlocks.length > 0) {
            return Array.from(languageBlocks).find((block) => {
                return !block.hasAttribute('lang');
            });
        }
        return rootElement;
    },

    orderGapChoiceModels(gapChoiceModels, tokenOrder) {
        if (tokenOrder === 'random') {
            return utilService.shuffleArray(gapChoiceModels);
        }
        return gapChoiceModels;
    },

    extractMediaModels(questionIdentifier, assessmentRunId, mediaInfo, rootElement) {
        const images = Array.from(rootElement.getElementsByTagName('img'));
        const mediaModels = [];
        images.forEach((image) => {
            const mediaModel = parseImage(questionIdentifier, image, assessmentRunId, mediaInfo);
            mediaModels.push(mediaModel);
            replaceMediaWithPlaceholder(image, questionIdentifier);
        });

        return mediaModels;
    },

    normalizeDom(doc, htmlString) {
        const gapsContainer = doc.querySelector('.gaps');
        if (!gapsContainer || !Array.from(gapsContainer?.childNodes).length) return htmlString;

        const paragraph = gapsContainer.querySelector('p');
        const paragraphCopy = paragraph.cloneNode(true);
        if (!paragraph) return htmlString;

        this.cleanParagraph(paragraphCopy);

        paragraph.innerHTML = '';
        this.createParagraphs(paragraphCopy, paragraph);

        return doc.body.innerHTML;
    },

    cleanParagraph(paragraph) {
        Array.from(paragraph.querySelectorAll('br')).forEach(br => br.parentNode.removeChild(br));

        Array.from(paragraph.childNodes)
            .filter(node => node.nodeType === Node.TEXT_NODE && !node.textContent.trim())
            .forEach(node => node.parentNode.removeChild(node));
    },

    createParagraphs(paragraph, gapsContainer) {
        const nodes = Array.from(paragraph.childNodes);

        for (let i = 0; i < nodes.length; i += 2) {
            const currentNode = nodes[i];
            const nextNode = nodes[i + 1];

            const newParagraph = document.createElement('p');
            newParagraph.className = 'gapParagraph';

            newParagraph.appendChild(document.createTextNode(currentNode.textContent.trim()));
            if (nextNode) newParagraph.appendChild(nextNode.cloneNode(true));

            gapsContainer.appendChild(newParagraph);
        }
    },

    parse(interactionNode, language, defaultLanguage, assessmentRunId, questionIdentifier, mediaInfo, qtiData) {
        const interactionNodeClone = interactionNode.cloneNode(true);
        const gapTextModels = this.extractGapTextModels(interactionNodeClone, language, defaultLanguage);
        const gapImgModels = this.extractGapImgModels(interactionNodeClone, language, defaultLanguage,
            assessmentRunId, questionIdentifier, mediaInfo);
        const mediaModels = this.extractMediaModels(questionIdentifier, assessmentRunId, mediaInfo, interactionNodeClone, language, defaultLanguage);
        const concatGapChoiceModels = gapTextModels.concat(gapImgModels);
        const currentLanguageBlockquote = this.extractInteractionBody(interactionNodeClone, language, defaultLanguage);
        const gapModels = this.extractGapModels(currentLanguageBlockquote);
        const prompt = this.extractPrompt(interactionNodeClone, language, defaultLanguage);
        const tokenHeader = this.extractTokenHeader(interactionNodeClone, language, defaultLanguage);
        const reuseAlternatives = interactionNodeClone.getAttribute('inspera:reuseAlternatives') === 'true' || false;
        const tokenOrder = interactionNodeClone.getAttribute('inspera:tokenOrder') || '';
        const gapChoiceModels = this.orderGapChoiceModels(concatGapChoiceModels, tokenOrder);
        const extendedRenderingActivated = interactionNodeClone.getAttribute('inspera:extendedRendering') === 'true' || false;
        const tokenPositionExtended = extendedRenderingActivated ? 'right' : (interactionNodeClone.getAttribute('inspera:tokenPosition') || '');
        const isPBRenderStyleEnabled = sessionReader.isPrintStylePaperBased() && settingsReader.isPBRenderForCeqEnabled();
        const tokenPosition = isPBRenderStyleEnabled ? (interactionNodeClone.getAttribute('inspera:tokenPosition') || '') : tokenPositionExtended;
        const shouldAutoSizeTokens = extendedRenderingActivated ? true : interactionNodeClone.getAttribute('inspera:tokenSize') === 'autoSize';

        this.parseGapMatchBody(currentLanguageBlockquote);


        const shouldNormalizeDomForPB =
            customFieldsService.isMatching1(qtiData) ||
            customFieldsService.isMatchingSentenceEnding(qtiData);

        const interactionBody = serializer.serializeToString(currentLanguageBlockquote).trim();

        const parser = new DOMParser();
        const doc = parser.parseFromString(interactionBody, 'text/html');
        const interactionBodyPB = shouldNormalizeDomForPB ? this.normalizeDom(doc, interactionBody) : null;

        return {
            gapModels,
            gapChoiceModels,
            interactionBody,
            interactionBodyPB,
            prompt,
            tokenHeader,
            mediaModels,
            reuseAlternatives,
            shouldAutoSizeTokens,
            tokenPosition,
            tokenOrder,
            extendedRenderingActivated,
        };
    },
};

export default gapMatchParser;

